import PropTypes from 'prop-types';
import React from 'react';
import { Core } from 'reactifi';
import i18n from 'lib/utility/i18n';
import NextStepCard from './NextStepCard';

// Design system tokens
const PAGE_BG = 'surface-neutral-subdued';
const UP_NEXT_BG = 'surface-inset';
const MORE_TO_EXPLORE_BG = 'surface-tertiary';

const renderSectionLabel = text => (
  <Core.Typography textStyle="p3" textTransform="uppercase" letterSpacing="wide" fontWeight="bold" mb="xs">
    {text}
  </Core.Typography>
);

const ModuleCompleteNextSteps = props => {
  const { completedModuleName, upNext, moreToExplore, onReturn, returnLabel, isGuest, onLogin } = props;

  return (
    <Core.Layout bg={PAGE_BG} minH="calc(100vh - 60px)" py="lg" px="md" data-attribute="module-complete-next-steps">
      <Core.Layout variant="stack" gap="md" maxW="800px" mx="auto">
        <Core.Card>
          <Core.CardBody>
            <Core.Layout variant="stack" gap="lg">
              <Core.Layout variant="stack" gap="xs">
                <Core.Typography textStyle="h2" m={0}>
                  {i18n.t('You completed {{ name }}!', { name: completedModuleName })}
                </Core.Typography>

                {isGuest && !!onLogin && (
                  <Core.Typography textStyle="p2" data-attribute="module-complete-guest-login">
                    <Core.Link as="button" onClick={onLogin} data-action="open-login-modal">
                      {i18n.t('Log in')}
                    </Core.Link>{' '}
                    {i18n.t('to save your progress and pick up where you left off.')}
                  </Core.Typography>
                )}
              </Core.Layout>

              {upNext && (
                <Core.Layout variant="stack" gap="0" data-attribute="module-complete-up-next">
                  {renderSectionLabel(i18n.t('Up Next'))}
                  <NextStepCard
                    item={upNext}
                    variant="filled"
                    bg={UP_NEXT_BG}
                    buttonVariant="primary"
                    mediaPosition="left"
                  />
                </Core.Layout>
              )}

              {!!moreToExplore?.length && (
                <Core.Layout variant="stack" gap="0" data-attribute="module-complete-more-to-explore">
                  {renderSectionLabel(i18n.t('More to Explore'))}
                  <Core.Layout
                    variant="grid"
                    gridTemplateColumns={{
                      base: '1fr',
                      md: moreToExplore.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                    }}
                    gap="md"
                    w="100%"
                  >
                    {moreToExplore.map(item => (
                      <NextStepCard
                        key={item.id || item.title}
                        item={item}
                        variant="outline"
                        bg={MORE_TO_EXPLORE_BG}
                        buttonVariant="secondary"
                      />
                    ))}
                  </Core.Layout>
                </Core.Layout>
              )}
            </Core.Layout>
          </Core.CardBody>
        </Core.Card>

        {onReturn && (
          <Core.Layout variant="flex" justifyContent="center">
            <Core.Link as="button" onClick={onReturn} textStyle="p2" data-action="next-steps-return">
              {returnLabel || i18n.t('Return to Pathway')}
            </Core.Link>
          </Core.Layout>
        )}
      </Core.Layout>
    </Core.Layout>
  );
};

const itemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  meta: PropTypes.string,
  buttonLabel: PropTypes.string,
  imageUrl: PropTypes.string,
  startUrl: PropTypes.string.isRequired,
});

ModuleCompleteNextSteps.propTypes = {
  completedModuleName: PropTypes.string.isRequired,
  upNext: itemShape,
  moreToExplore: PropTypes.arrayOf(itemShape),
  onReturn: PropTypes.func,
  returnLabel: PropTypes.string,
  isGuest: PropTypes.bool,
  onLogin: PropTypes.func,
};

export default ModuleCompleteNextSteps;
